import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { from, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AppConfigService } from '../services/app-config.service';

/**
 * Blocks access to phase-gated routes based on /config/app.appPhase.
 * Admins (claim admin:true OR users/{uid}.role == 'admin') always bypass.
 *
 * landing     → redirect to /proximamente
 * maintenance → redirect to /mantenimiento
 * production  → allow (existing guards apply)
 */
export const phaseGuard: CanActivateFn = (_route, _state) => {
  const configSvc = inject(AppConfigService);
  const auth = inject(Auth);
  const firestore = inject(Firestore);
  const router = inject(Router);
  const injector = inject(EnvironmentInjector);

  return from(configSvc.config$.pipe(take(1))).pipe(
    switchMap(config => {
      const phase = config.appPhase ?? 'production';
      if (phase === 'production') return of(true as const);

      return authState(auth).pipe(
        take(1),
        switchMap(firebaseUser => {
          if (!firebaseUser) {
            return of(phaseRedirect(router, phase));
          }

          return from(firebaseUser.getIdTokenResult()).pipe(
            switchMap(tokenResult => {
              if (tokenResult.claims['admin'] === true) return of(true as const);

              const fetch = runInInjectionContext(injector, () =>
                getDoc(doc(firestore, 'users', firebaseUser.uid)),
              );
              return from(fetch).pipe(
                map(snap => {
                  const role = snap.exists() ? snap.data()['role'] : null;
                  if (role === 'admin') return true as const;
                  return phaseRedirect(router, phase);
                }),
              );
            }),
            catchError(() => of(phaseRedirect(router, phase))),
          );
        }),
      );
    }),
  );
};

function phaseRedirect(router: Router, phase: string) {
  if (phase === 'landing') return router.createUrlTree(['/proximamente']);
  if (phase === 'maintenance') return router.createUrlTree(['/mantenimiento']);
  return true as const;
}
